// The single source of truth for what each real item LOOKS like, everywhere.
// Each of the 8 items is a small vector silhouette on a 24×24 grid, drawn
// upright (handle vertical, head at the top) and then leaned 45° like a tool
// propped against a wall — one uniform composition, one stroke width.
//
// Two consumers share the same path data:
//   - itemIconSvg(): inline <svg> strings for DOM chips/buttons (currentColor)
//   - drawItemGlyph() in render/entities.ts: canvas Path2D for gear-swap
//     flights and items dropped on the ground
// Owner: client-ui.

import type { ItemType } from '../../shared/types'

/** Which material a path segment is, so canvas renderers can tint wood vs steel.
 *  SVG renders everything in currentColor and ignores this. */
export type ItemInk = 'wood' | 'steel' | 'string'

export interface ItemPathSeg {
  /** SVG path data in the upright 24×24 frame (pre-rotation). */
  d: string
  /** Filled silhouette (heads/blades); otherwise stroked (handles/shafts). */
  fill?: boolean
  /** Stroke width override (default 1.8). */
  w?: number
  ink: ItemInk
}

/** Upright geometry per item. Handles run up x=12; heads sit at the top. */
export const ITEM_PATHS: Record<ItemType, ItemPathSeg[]> = {
  // Claw/mallet head set square across a full-length handle.
  // Wooden tool: the head is a carved mallet, not forged (wood-only cost).
  hammer: [
    { d: 'M12 21.5 L12 8.6', ink: 'wood' },
    { d: 'M7 4.8 L17 4.8 L17 8.3 L7 8.3 Z', fill: true, ink: 'wood' },
  ],
  // The smith's: a heavier, squarer block on a stubby handle.
  smithhammer: [
    { d: 'M12 19.6 L12 10.4', ink: 'wood' },
    { d: 'M7.4 3.8 L16.6 3.8 L16.6 10.4 L7.4 10.4 Z', fill: true, ink: 'steel' },
  ],
  // Long handle, flat blade set at an angle off the top — a field hoe,
  // NOT a sheaf of wheat. Wooden tool: the blade is carved (wood-only cost).
  hoe: [
    { d: 'M12 21.5 L12 6.6', ink: 'wood' },
    { d: 'M11.4 6.8 L18.4 4.4 L19.6 7.6 L12.6 10 Z', fill: true, ink: 'wood' },
  ],
  // Straight haft with a rounded spade blade at the tip.
  // Wooden tool: the blade is carved (wood-only cost).
  shovel: [
    { d: 'M12 21.5 L12 8.5', ink: 'wood' },
    { d: 'M9 8.5 L15 8.5 L15 4.8 Q12 2.2 9 4.8 Z', fill: true, ink: 'wood' },
  ],
  // Wedge blade with a convex cutting edge on one side of the haft.
  // Wooden tool: the head is carved, not forged (see ITEMS — wood-only cost).
  axe: [
    { d: 'M12 21.5 L12 5.4', ink: 'wood' },
    { d: 'M12 4.8 Q16.6 4.2 19.2 6.2 Q20 8.4 19.2 10.8 Q16.6 10.2 12 10.2 Z', fill: true, ink: 'wood' },
  ],
  // Long slender pole with the line arcing off the tip to a hook.
  rod: [
    { d: 'M10.5 21.5 L13.5 3.2', ink: 'wood' },
    { d: 'M13.5 3.2 Q18.5 5.4 18.2 11.2', ink: 'string' },
    { d: 'M18.2 11.2 Q18.2 13.4 16.4 13 Q15.4 12.4 16.4 11.2', ink: 'steel' },
  ],
  // Curved twin-point crescent arcing over the top of the handle.
  // Wooden tool: the head is carved, not forged (see ITEMS — wood-only cost).
  pick: [
    { d: 'M12 21.5 L12 6.4', ink: 'wood' },
    { d: 'M4.4 9.6 Q12 1.8 19.6 9.6 Q12 4.9 4.4 9.6 Z', fill: true, ink: 'wood' },
  ],
  // Long shaft, leaf-shaped tip. Wood-tier weapon: a fire-hardened wooden
  // point, whittled like the tools (wood-only cost).
  spear: [
    { d: 'M12 22 L12 8.2', ink: 'wood' },
    { d: 'M12 2 Q14.6 5.2 12 8.8 Q9.4 5.2 12 2 Z', fill: true, ink: 'wood' },
  ],
  // Straight tapered blade, crossguard, grip, pommel.
  sword: [
    { d: 'M12 2.2 L13.5 5 L13.5 14.2 L10.5 14.2 L10.5 5 Z', fill: true, ink: 'steel' },
    { d: 'M8.2 14.8 L15.8 14.8', ink: 'steel' },
    { d: 'M12 15.4 L12 19.4', ink: 'wood' },
    { d: 'M12 21.6 a1.3 1.3 0 1 0 0 -2.6 a1.3 1.3 0 1 0 0 2.6 Z', fill: true, ink: 'wood' },
  ],
  // Curved stave, straight string.
  bow: [
    { d: 'M8.6 3.6 Q19.6 12 8.6 20.4', ink: 'wood' },
    { d: 'M8.6 3.6 L8.6 20.4', w: 1, ink: 'string' },
  ],
}

/** Every item leans the same way, like tools propped in a rack. */
export const ITEM_LEAN_DEG = 45

/** Inline SVG for an item, self-contained and theme-friendly: currentColor
 *  strokes/fills so it inherits the surrounding text color. `size` is the
 *  square px box (defaults to sitting nicely in body text). */
export function itemIconSvg(item: ItemType, size = 16): string {
  const parts = ITEM_PATHS[item]
    .map((p) =>
      p.fill
        ? `<path d="${p.d}" fill="currentColor" stroke="none"/>`
        : `<path d="${p.d}"${p.w !== undefined ? ` stroke-width="${p.w}"` : ''}/>`,
    )
    .join('')
  return (
    `<svg class="item-ico" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"` +
    ` width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.8"` +
    ` stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"` +
    ` style="vertical-align:-0.125em"><g transform="rotate(${ITEM_LEAN_DEG} 12 12)">${parts}</g></svg>`
  )
}
